<template>
  <v-card>
    <v-card-title class="primary white--text">
      <v-row>
        <v-col>
          <span class="headline">{{ $t('editRate') }}</span>
        </v-col>
        <v-col cols="auto">
          <BaseDialogActions hideRefresh hideFullscreen/>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="pt-5" v-if="initialized">
      <v-row :dense="$vuetify.breakpoint.height < DENSE_THRESHOLD" :no-gutters="$vuetify.breakpoint.height < NO_GUTTERS_THRESHOLD">
        <v-col cols="12">
          <v-card flat :color="attrCardColor">
            <v-card-text :class="attrCardClass">
              <v-text-field
              :class="readOnlyTextFieldClass"
              dense
              readonly
              :label="$t('speciesGroup')"
              v-model="rateSheetLineClone.speciesGroup.label"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card flat :color="attrCardColor">
            <v-card-text :class="attrCardClass">
              <v-text-field
              :class="readOnlyTextFieldClass"
              dense
              readonly
              :label="$t('lengthGroup')"
              v-model="rateSheetLineClone.lengthGroup.label"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card flat :color="attrCardColor">
            <v-card-text :class="attrCardClass">
              <v-text-field
              :class="readOnlyTextFieldClass"
              dense
              readonly
              :label="$t('diameterGroup')"
              v-model="rateSheetLineClone.diameterGroup.label"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card flat :color="attrCardColor">
            <v-card-text :class="attrCardClass">
              <v-text-field
              :class="readOnlyTextFieldClass"
              dense
              readonly
              :label="$t('grade')"
              v-model="rateSheetLineClone.grade.name"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card flat :color="attrCardColor">
            <v-card-text :class="attrCardClass">
              <v-text-field
              :class="readOnlyTextFieldClass"
              dense
              readonly
              :label="$t('endUseSort')"
              v-model="rateSheetLineClone.endUseSort.name"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col>
          <MoneyTextField
          v-if="initialized && rateRerenderTrigger"
          :initialValue="rate"
          :label="$t('rate')"
          :focus="focusRate"
          @val-changed="rateEntered"
          elementId="rate">
          <template #append v-if="showRevertButton">
            <Icon :small="false" icon="mdi-arrow-u-left-top-bold" @icon-clicked="onRevertPressed"/>
          </template>
          </MoneyTextField>
        </v-col>
      </v-row>

    </v-card-text>
    <v-card-actions class="py-4 px-0">
      <v-row justify="space-between">
        <v-col>

        </v-col>
        <v-col cols="auto">
          <v-btn color="primary" @click="confirmClicked">
            {{ $t('confirm') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import { cloneDeep } from 'lodash'

const DENSE_THRESHOLD = 850
const NO_GUTTERS_THRESHOLD = 650

export default {
  name: 'RateSheetLineForm',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    MoneyTextField: () => import('@/components/helper/MoneyTextField.vue')
  },

  props: {
    rateSheetLine: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    DENSE_THRESHOLD,
    NO_GUTTERS_THRESHOLD,
    initialized: false,
    rate: null,
    focusRate: false,
    rateRerenderTrigger: true,
    rateSheetLineClone: null
  }),

  computed: {
    attrCardColor () {
      return this.$vuetify.breakpoint.height < NO_GUTTERS_THRESHOLD ? 'transparent' : 'grey lighten-4'
    },
    attrCardClass () {
      return this.$vuetify.breakpoint.height < NO_GUTTERS_THRESHOLD ? 'px-0 my-n1 prevent-select' : 'prevent-select'
    },
    readOnlyTextFieldClass () {
      return 'mb-n6 prevent-select'
    },
    showRevertButton () {
      if (this.rateSheetLine.rateFromDefinition !== undefined) {
        return this.rate !== this.rateSheetLine.rateFromDefinition
      } else if (this.rateSheetLine.rateFromPreexistingSheet !== undefined) {
        return this.rate !== this.rateSheetLine.rateFromPreexistingSheet
      } else {
        return false
      }
    }
  },

  mounted () {
    this.initialize()
    this.initialized = true
    this.$nextTick(() => {
      this.focusRate = true
    })
  },

  methods: {
    initialize () {
      this.rateSheetLineClone = cloneDeep(this.rateSheetLine)
      this.rateSheetLineClone.speciesGroup = this.rateSheetLineClone.speciesGroup || { label: this.$t('notSpecified') }
      this.rateSheetLineClone.lengthGroup = this.rateSheetLineClone.lengthGroup || { label: this.$t('notSpecified') }
      this.rateSheetLineClone.diameterGroup = this.rateSheetLineClone.diameterGroup || { label: this.$t('notSpecified') }
      this.rateSheetLineClone.grade = this.rateSheetLineClone.grade || { name: this.$t('notSpecified') }
      this.rateSheetLineClone.endUseSort = this.rateSheetLineClone.endUseSort || { name: this.$t('notSpecified') }
      this.rate = this.rateSheetLineClone.rate
    },

    rateEntered (value) {
      this.rate = value
    },

    confirmClicked () {
      this.$emit('confirm', this.rate)
    },

    onRevertPressed () {
      const rateToRevertTo = this.rateSheetLine.rateFromDefinition !== undefined
        ? this.rateSheetLine.rateFromDefinition
        : this.rateSheetLine.rateFromPreexistingSheet
      this.rateEntered(rateToRevertTo)
      this.rateRerenderTrigger = false
      this.$nextTick(() => {
        this.rateRerenderTrigger = true
      })
    }
  }
}
</script>

<style>
.prevent-select, .prevent-select .v-text-field__slot * {
  pointer-events: none;
  user-select: none;
}
</style>
